/**
 * HistoryRail — left sidebar inside the Apex drawer.
 *
 * Renders the list of saved chats (from ApexProvider), grouped into
 * Today / Yesterday / Past 7 days / Older buckets. Each row supports
 * load (click), rename (pencil), and delete (trash + confirm).
 */

import { useMemo, useState } from "react";
import {
  App,
  Button,
  Dropdown,
  Empty,
  Input,
  List,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { ditColors } from "../../lib/theme";
import { useApex } from "./ApexProvider";
import type { ChatSummary } from "../../lib/api";

dayjs.extend(relativeTime);

type Bucket = "Today" | "Yesterday" | "Past 7 days" | "Older";

const BUCKET_ORDER: Bucket[] = ["Today", "Yesterday", "Past 7 days", "Older"];

function bucketFor(ts?: string): Bucket {
  if (!ts) return "Older";
  const t = dayjs(ts);
  if (!t.isValid()) return "Older";
  const now = dayjs();
  if (t.isSame(now, "day")) return "Today";
  if (t.isSame(now.subtract(1, "day"), "day")) return "Yesterday";
  if (t.isAfter(now.subtract(7, "day"))) return "Past 7 days";
  return "Older";
}

export default function HistoryRail() {
  const {
    chats,
    currentChatId,
    newChat,
    loadChat,
    deleteChat,
    renameChat,
    loadingChats,
  } = useApex();
  const { modal } = App.useApp();

  const [renaming, setRenaming] = useState<{ chatId: string; title: string } | null>(null);

  const grouped = useMemo(() => {
    const map: Record<Bucket, ChatSummary[]> = {
      Today: [],
      Yesterday: [],
      "Past 7 days": [],
      Older: [],
    };
    for (const chat of chats) {
      const ts = chat.lastMessageAt || chat.updatedAt;
      map[bucketFor(ts)].push(chat);
    }
    return map;
  }, [chats]);

  const handleRenameSubmit = async () => {
    if (!renaming) return;
    const trimmed = renaming.title.trim();
    if (!trimmed) {
      setRenaming(null);
      return;
    }
    await renameChat(renaming.chatId, trimmed);
    setRenaming(null);
  };

  return (
    <div
      style={{
        width: 220,
        borderRight: `1px solid ${ditColors.secondaryLight}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 12,
        background: "#fff",
      }}
      aria-label="Chat history"
    >
      <Button
        type="primary"
        block
        icon={<PlusOutlined aria-hidden />}
        onClick={newChat}
        style={{ background: ditColors.primary, borderColor: ditColors.primary }}
      >
        New chat
      </Button>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {chats.length === 0 ? (
          loadingChats ? (
            <Typography.Text type="secondary">Loading...</Typography.Text>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No chats yet"
              styles={{ image: { height: 40 } }}
            />
          )
        ) : (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {BUCKET_ORDER.map((bucket) => {
              const items = grouped[bucket];
              if (!items?.length) return null;
              return (
                <div key={bucket}>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}
                  >
                    {bucket}
                  </Typography.Text>
                  <List
                    size="small"
                    dataSource={items}
                    split={false}
                    renderItem={(chat) => {
                      const isActive = chat.chatId === currentChatId;
                      const ts = chat.lastMessageAt || chat.updatedAt;
                      const rel = ts && dayjs(ts).isValid() ? dayjs(ts).fromNow() : "";
                      const title = chat.chatTitle || "Untitled chat";
                      return (
                        <List.Item
                          key={chat.chatId}
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            background: isActive ? ditColors.secondaryLight : "transparent",
                            cursor: "pointer",
                          }}
                          onClick={() => loadChat(chat.chatId)}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              width: "100%",
                              minWidth: 0,
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Typography.Text
                                strong={isActive}
                                title={title}
                                style={{
                                  color: ditColors.primary,
                                  fontSize: 13,
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {title}
                              </Typography.Text>
                              <Space size={6} style={{ marginTop: 2 }}>
                                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                  {rel}
                                </Typography.Text>
                                {typeof chat.messageCount === "number" ? (
                                  <Tag
                                    color="default"
                                    style={{
                                      fontSize: 10,
                                      lineHeight: "16px",
                                      padding: "0 6px",
                                      margin: 0,
                                      background: "#fff",
                                      borderColor: ditColors.secondaryLight,
                                      color: ditColors.primary,
                                    }}
                                  >
                                    {chat.messageCount}
                                  </Tag>
                                ) : null}
                              </Space>
                            </div>
                            <Dropdown
                              trigger={["click"]}
                              placement="bottomRight"
                              menu={{
                                items: [
                                  {
                                    key: "rename",
                                    icon: <EditOutlined aria-hidden />,
                                    label: "Rename",
                                    onClick: ({ domEvent }) => {
                                      domEvent.stopPropagation();
                                      setRenaming({
                                        chatId: chat.chatId,
                                        title: chat.chatTitle || "",
                                      });
                                    },
                                  },
                                  {
                                    key: "delete",
                                    icon: <DeleteOutlined aria-hidden />,
                                    label: "Delete",
                                    danger: true,
                                    onClick: ({ domEvent }) => {
                                      domEvent.stopPropagation();
                                      modal.confirm({
                                        title: "Delete this chat?",
                                        content: title,
                                        okText: "Delete",
                                        okButtonProps: { danger: true },
                                        onOk: () => deleteChat(chat.chatId),
                                      });
                                    },
                                  },
                                ],
                              }}
                            >
                              <Button
                                size="small"
                                type="text"
                                icon={<MoreOutlined aria-hidden />}
                                aria-label={`More actions for chat ${title}`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Dropdown>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              );
            })}
          </Space>
        )}
      </div>

      <Modal
        open={!!renaming}
        title="Rename chat"
        onCancel={() => setRenaming(null)}
        onOk={handleRenameSubmit}
        okText="Save"
        destroyOnHidden
      >
        <Input
          value={renaming?.title ?? ""}
          onChange={(e) =>
            setRenaming((prev) => (prev ? { ...prev, title: e.target.value } : prev))
          }
          onPressEnter={handleRenameSubmit}
          placeholder="Chat title"
          aria-label="Chat title"
          autoFocus
        />
      </Modal>
    </div>
  );
}
